import { useEffect, useId, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import './CsvFormatHelpModal.css';

export type CsvFormatHelpModalProps = {
  open: boolean;
  onClose: () => void;
  onDownloadTemplate: () => void;
};

type ColumnSpec = {
  key: string; // CSV column name as it appears in the template header
  required: boolean;
  /** i18n token suffix for the description / range / example cells. */
  i18nToken: string;
};

// Order matches CSV_TEMPLATE_HEADERS so the table mirrors what the admin sees
// when they open the downloaded template in Excel.
const COLUMNS: ColumnSpec[] = [
  { key: 'type', required: true, i18nToken: 'type' },
  { key: 'zone', required: true, i18nToken: 'zone' },
  { key: 'community', required: true, i18nToken: 'community' },
  { key: 'building', required: false, i18nToken: 'building' },
  { key: 'bedrooms', required: true, i18nToken: 'bedrooms' },
  { key: 'area', required: true, i18nToken: 'area' },
  { key: 'bathrooms', required: false, i18nToken: 'bathrooms' },
  { key: 'floor', required: false, i18nToken: 'floor' },
  { key: 'handover', required: false, i18nToken: 'handover' },
  { key: 'title', required: true, i18nToken: 'title' },
  { key: 'description', required: true, i18nToken: 'description' },
  { key: 'price_aed', required: true, i18nToken: 'price_aed' },
  { key: 'original_price', required: false, i18nToken: 'original_price' },
  { key: 'amount_paid', required: false, i18nToken: 'amount_paid' },
];

/**
 * Modal explaining the CSV import format. Reuses the
 * `<CatalogueFilterModal>` skeleton (sticky header / footer, focus trap, ESC,
 * scroll lock, RTL via logical properties). Footer hosts the "Download
 * template" CTA so the admin can grab a valid file in one click without
 * leaving the dialog.
 */
export function CsvFormatHelpModal({ open, onClose, onDownloadTemplate }: CsvFormatHelpModalProps) {
  const { t } = useTranslation();
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    closeBtnRef.current?.focus();
    const trap = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' || !dialogRef.current) return;
      const focusables = dialogRef.current.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', trap);
    return () => window.removeEventListener('keydown', trap);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="csv-format-help-modal__backdrop"
      role="presentation"
      onClick={onClose}
      data-testid="csv-format-help-modal-backdrop"
    >
      <div
        ref={dialogRef}
        className="csv-format-help-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(event) => event.stopPropagation()}
      >
        <header className="csv-format-help-modal__header">
          <h2 id={titleId} className="csv-format-help-modal__title">
            {t('admin.import.help.modalTitle')}
          </h2>
          <button
            ref={closeBtnRef}
            type="button"
            onClick={onClose}
            aria-label={t('admin.import.help.close')}
            className="csv-format-help-modal__close"
          >
            ×
          </button>
        </header>
        <div className="csv-format-help-modal__body">
          <p className="csv-format-help-modal__intro">{t('admin.import.help.intro')}</p>
          <table className="csv-format-help-modal__table">
            <thead>
              <tr>
                <th>{t('admin.import.help.columns.name')}</th>
                <th>{t('admin.import.help.columns.required')}</th>
                <th>{t('admin.import.help.columns.format')}</th>
                <th>{t('admin.import.help.columns.example')}</th>
              </tr>
            </thead>
            <tbody>
              {COLUMNS.map((col) => (
                <tr key={col.key}>
                  <td className="csv-format-help-modal__cell--mono">{col.key}</td>
                  <td>
                    {col.required ? (
                      <span className="csv-format-help-modal__pill csv-format-help-modal__pill--required">
                        {t('admin.import.help.required')}
                      </span>
                    ) : (
                      <span className="csv-format-help-modal__pill">
                        {t('admin.import.help.optional')}
                      </span>
                    )}
                  </td>
                  <td>{t(`admin.import.help.rows.${col.i18nToken}.format`)}</td>
                  <td className="csv-format-help-modal__cell--mono">
                    {t(`admin.import.help.rows.${col.i18nToken}.example`)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="csv-format-help-modal__footnote">{t('admin.import.help.encodingNote')}</p>
        </div>
        <footer className="csv-format-help-modal__footer">
          <button
            type="button"
            onClick={onClose}
            className="csv-format-help-modal__btn csv-format-help-modal__btn--secondary"
          >
            {t('admin.import.help.close')}
          </button>
          <button
            type="button"
            onClick={() => {
              onDownloadTemplate();
              onClose();
            }}
            className="csv-format-help-modal__btn csv-format-help-modal__btn--primary"
          >
            {t('admin.import.downloadTemplate')}
          </button>
        </footer>
      </div>
    </div>
  );
}
